import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import FormType from './FormType';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { ExclamationTriangleIcon, ArrowRightIcon } from '@radix-ui/react-icons';
import { FormTypeSkeleton } from './SkeletonList';
import useAnalytics from '../hooks/useAnalytics';

const FORM_TYPE_CARD_BASE_CLASSES = 'group relative bg-white rounded-2xl border-2 border-gray-200 hover:border-black hover:shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2';
const FORM_TYPE_CATEGORY_SEPARATOR = ' - ';

const getFormTypeParts = (formType) => {
  const name = formType?.name || '';
  const separatorIndex = name.indexOf(FORM_TYPE_CATEGORY_SEPARATOR);

  if (separatorIndex <= 0) {
    return { category: null, entryName: name };
  }

  const category = name.slice(0, separatorIndex).trim();
  const entryName = name.slice(separatorIndex + FORM_TYPE_CATEGORY_SEPARATOR.length).trim();

  if (!category || !entryName) {
    return { category: null, entryName: name };
  }

  return { category, entryName };
};

const buildFormTypeNavigationItems = (formTypes) => {
  const categories = new Map();
  const items = [];

  formTypes.forEach((formType) => {
    const parts = getFormTypeParts(formType);

    if (!parts.category) {
      items.push({ type: 'entry', formType, label: parts.entryName });
      return;
    }

    if (!categories.has(parts.category)) {
      const categoryItem = {
        type: 'category',
        category: parts.category,
        entries: [],
      };

      categories.set(parts.category, categoryItem);
      items.push(categoryItem);
    }

    categories.get(parts.category).entries.push({
      type: 'entry',
      formType,
      label: parts.entryName,
    });
  });

  return {
    hasCategories: categories.size > 0,
    items,
    categories,
  };
};

const getFormTypeLayoutClasses = (formTypeCount) => {
  if (formTypeCount > 5) {
    return {
      grid: 'md:grid-cols-3 gap-3',
      card: `${FORM_TYPE_CARD_BASE_CLASSES} p-3`,
      title: 'text-base mb-2',
      description: 'text-xs mb-2',
      action: 'mt-2',
    };
  }

  if (formTypeCount > 2) {
    return {
      grid: 'md:grid-cols-2 gap-4',
      card: `${FORM_TYPE_CARD_BASE_CLASSES} p-4`,
      title: 'text-lg mb-2',
      description: 'text-sm mb-3',
      action: 'mt-3',
    };
  }

  return {
    grid: `${formTypeCount > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-6`,
    card: `${FORM_TYPE_CARD_BASE_CLASSES} p-8`,
    title: 'text-xl mb-3',
    description: 'text-sm mb-4',
    action: 'mt-4',
  };
};

function Form({
  userAccountId,
  userName,
  userSectors,
  userRole,
  userMode,
  userServiceDeskId,
  userServiceDeskName,
  deviceIsInWebView = false,
}) {
  const navigate = useNavigate();
  const { requestTypeId } = useParams();
  const [searchParams] = useSearchParams();
  const requestTypeIdFromUrl = requestTypeId || searchParams.get('requestTypeId');
  const prefillInterventionPointKey = searchParams.get('interventionPoint');
  const prefillFurnitureKey = searchParams.get('furniture');
  const isTechnician = userRole === 'technician';
  const [serviceDesks, setServiceDesks] = useState([]);
  const [selectedServiceDeskId, setSelectedServiceDeskId] = useState(
    isTechnician ? localStorage.getItem('selected-service-desk') : userServiceDeskId
  );
  const [formTypes, setFormTypes] = useState([]);
  const formTypeNavigation = useMemo(() => buildFormTypeNavigationItems(formTypes), [formTypes]);
  const [selectedCategoryName, setSelectedCategoryName] = useState(null);
  const selectedCategory = selectedCategoryName
    ? formTypeNavigation.categories.get(selectedCategoryName)
    : null;
  const displayedFormTypeItems = selectedCategory
    ? selectedCategory.entries
    : formTypeNavigation.items;
  const formTypeLayoutClasses = getFormTypeLayoutClasses(displayedFormTypeItems.length);
  const [selectedFormType, setSelectedFormType] = useState(requestTypeIdFromUrl || null);
  const [isLoading, setIsLoading] = useState(!isTechnician || selectedServiceDeskId);
  const [isFormTypeLoading, setIsFormTypeLoading] = useState(false);

  const [formTypeFields, setFormTypeFields] = useState(null);

  useAnalytics({
    currentUser: userName,
    project: userServiceDeskName,
    role: userRole,
    module: 'create-request',
  });

  const handleServiceDeskChange = (serviceDeskId) => {
    setSelectedServiceDeskId(serviceDeskId);
    localStorage.setItem('selected-service-desk', serviceDeskId);
    setFormTypes([]);
    setSelectedFormType(null);
    setSelectedCategoryName(null);
    setFormTypeFields(null);
    navigate('/request');
  };

  const handleFormTypeSelect = (formTypeId) => {
    const nextFormTypeId = String(formTypeId);
    const nextSearchParams = new URLSearchParams();

    if (prefillInterventionPointKey) {
      nextSearchParams.set('interventionPoint', prefillInterventionPointKey);
    }

    if (prefillFurnitureKey) {
      nextSearchParams.set('furniture', prefillFurnitureKey);
    }

    const queryString = nextSearchParams.toString();

    setSelectedFormType(nextFormTypeId);
    setFormTypeFields(null);
    navigate(`/request/${nextFormTypeId}${queryString ? `?${queryString}` : ''}`);
  };

  const handleFormTypeCancel = () => {
    setSelectedFormType(null);
    setFormTypeFields(null);
    navigate('/request');
  };

  useEffect(() => {
    setSelectedFormType(requestTypeIdFromUrl || null);
    setFormTypeFields(null);
  }, [requestTypeIdFromUrl]);

  useEffect(() => {
    if (!isTechnician) return;

    const fetchServiceDesks = async () => {
      try {
        const response = await fetchWithAuth('service-desks');
        const result = await response.json();
        const desks = result.data || [];
        setServiceDesks(desks);

        // Auto-select if only one service desk available and nothing in localStorage
        if (desks.length === 1 && !selectedServiceDeskId) {
          const firstDeskId = String(desks[0].id);
          setSelectedServiceDeskId(firstDeskId);
          localStorage.setItem('selected-service-desk', firstDeskId);
        }
      } catch (error) {
        console.error('Error fetching service desks:', error);
      }
    };

    fetchServiceDesks();
  }, [isTechnician]);

  useEffect(() => {
    if (isTechnician && !selectedServiceDeskId) {
      setIsLoading(false);
      return;
    }

    const fetchFormTypes = async () => {
      try {
        const url = isTechnician && selectedServiceDeskId
          ? `form-types?serviceDeskId=${selectedServiceDeskId}`
          : 'form-types';

        const response = await fetchWithAuth(url, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Account-Id': userAccountId,
          },
        });
        const data = await response.json();

        setFormTypes(data.values);
      } catch (error) {
        console.error('Error fetching form types:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFormTypes();
  }, [selectedServiceDeskId]);

  useEffect(() => {
    if (!selectedFormType) return;
    if (isTechnician && !selectedServiceDeskId) return;

    setIsFormTypeLoading(true);
    setFormTypeFields(null);
    const fetchFormTypeFields = async () => {
      try {
        const url = isTechnician && selectedServiceDeskId
          ? `form-types/${selectedFormType}?serviceDeskId=${selectedServiceDeskId}`
          : `form-types/${selectedFormType}`;

        const response = await fetchWithAuth(url);
        const data = await response.json();

        if (data && Array.isArray(data.requestTypeFields)) {
          setFormTypeFields(data);
        } else {
          console.error('[Form] Invalid formTypeFields data structure:', data);
          setFormTypeFields({ requestTypeFields: [] });
        }
      } catch (error) {
        console.error('Error fetching form type fields:', error);
        setFormTypeFields({ requestTypeFields: [] });
      } finally {
        setIsFormTypeLoading(false);
      }
    };

    fetchFormTypeFields();
  }, [selectedFormType, isTechnician, selectedServiceDeskId]);

  if (userMode === 'r') {
    return (
      <div className="h-full flex flex-col items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-6">
            <ExclamationTriangleIcon className="w-8 h-8 text-gray-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Accès restreint</h3>
          <p className="text-gray-600 leading-relaxed">
            Vous n'avez pas la permission de créer des demandes. Contactez votre administrateur pour plus d'informations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="w-full max-w-7xl mx-auto py-4">
          {/* Service Desk Selector for Technicians */}
          {isTechnician && !selectedFormType && (
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Nouvelle demande</h1>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <label htmlFor="service-desk-select" className="block text-sm font-medium text-gray-900 mb-2">
                  Sélectionnez un projet
                </label>
                <select
                  id="service-desk-select"
                  value={selectedServiceDeskId || ''}
                  onChange={(e) => handleServiceDeskChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                >
                  <option value="" disabled>
                    Sélectionnez un projet
                  </option>
                  {serviceDesks.map((sd) => (
                    <option key={sd.id} value={sd.id}>
                      {sd.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Header */}
          {!isTechnician && !selectedFormType && !isLoading && (
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900">Nouvelle demande</h1>
              <p className="text-gray-600">Sélectionnez le type de demande que vous souhaitez créer</p>
            </div>
          )}

          {/* Empty state for technicians without service desk */}
          {isTechnician && !selectedServiceDeskId && !isLoading && (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500 text-center">
                Veuillez sélectionner un projet pour afficher les types de demande disponibles.
              </p>
            </div>
          )}

          {/* Loading skeleton for form types */}
          {!selectedFormType && isLoading && <FormTypeSkeleton numberOfCards={2} />}

          {/* Form type selection */}
          {!selectedFormType && !isLoading && formTypes.length > 0 && (
            <>
              {selectedCategory && (
                <div className="mb-6 flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{selectedCategory.category}</h2>
                    <p className="text-sm text-gray-600">Sélectionnez le type de demande</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedCategoryName(null)}
                    className="text-sm font-medium text-gray-700 hover:text-black"
                  >
                    Retour aux catégories
                  </button>
                </div>
              )}

              <div className={`grid grid-cols-1 w-full ${formTypeLayoutClasses.grid}`}>
                {displayedFormTypeItems.map((item) => (
                  <button
                    key={item.type === 'category' ? `category-${item.category}` : item.formType.id}
                    onClick={() => item.type === 'category'
                      ? setSelectedCategoryName(item.category)
                      : handleFormTypeSelect(item.formType.id)}
                    className={formTypeLayoutClasses.card}
                  >
                    <div className="flex flex-col items-center text-center">
                      {/* Icon/Emoji */}
                      {/* {PICTO_FORMS[formType.id] && (
                      <div className="text-7xl mb-6 transform group-hover:scale-110 transition-transform duration-300">
                        {PICTO_FORMS[formType.id]}
                      </div>
                    )} */}

                      {/* Title */}
                      <h3 className={`${formTypeLayoutClasses.title} font-semibold text-gray-900 group-hover:text-black transition-colors`}>
                        {item.type === 'category' ? item.category : item.label}
                      </h3>

                      {/* Description */}
                      {item.type === 'entry' && item.formType.description && (
                        <p className={`${formTypeLayoutClasses.description} text-gray-600 leading-relaxed`}>
                          {item.formType.description}
                        </p>
                      )}

                      {/* Arrow indicator */}
                      <div className={`${formTypeLayoutClasses.action} inline-flex items-center gap-2 text-sm font-medium text-gray-900 group-hover:text-black`}>
                        <span>{item.type === 'category' ? 'Voir les types de demandes' : 'Continuer'}</span>
                        <ArrowRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Selected form type */}
          {selectedFormType && (
            <div className="flex flex-col items-center mb-8">
              <div className="w-full max-w-4xl">
                <h2 className="text-2xl font-bold mb-8 text-gray-900 text-center">
                  {formTypes.find(formType => String(formType.id) === selectedFormType)?.name}
                </h2>

                {/* Loading state for form fields */}
                {isFormTypeLoading && (
                  <div className="space-y-6">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="animate-pulse bg-white rounded-xl border border-gray-200 h-20"
                      />
                    ))}
                  </div>
                )}

                {/* Form fields */}
                {formTypeFields && (
                  <FormType
                    formTypeFields={formTypeFields.requestTypeFields}
                    selectedFormType={selectedFormType}
                    selectedFormTypeName={formTypes.find(formType => String(formType.id) === selectedFormType)?.name}
                    onCancel={handleFormTypeCancel}
                    userAccountId={userAccountId}
                    userSectors={userSectors}
                    userRole={userRole}
                    deviceIsInWebView={deviceIsInWebView}
                    serviceDeskId={selectedServiceDeskId}
                    prefillInterventionPointKey={prefillInterventionPointKey}
                    prefillFurnitureKey={prefillFurnitureKey}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Form;